/*
 * BBC - Rap Map - Map
 *
 * @author Matt Haynes <matt@joipolloi.com>
 * @package BBC Rap Map
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactMapGL, { NavigationControl, FlyToInterpolator } from 'react-map-gl';
import useSupercluster from 'use-supercluster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo, faCompress } from '@fortawesome/free-solid-svg-icons';

import CustomMarker from './CustomMarker';
import CustomPopup from './CustomPopup';
import Error from './Error';

import borderTopImage from './assets/border-top.png';
import borderRightImage from './assets/border-right.png';
import RapperOfTheWeekButton from './RapperOfTheWeekButton';

const mapStyle = {
    width: '100%',
    height: '100vh'
};

const initialViewport = {
    longitude: -3.74,
    latitude: 55.0,
    zoom: 5,
    minZoom: 5,
    maxZoom: 15,
    boundArea: {
        maxLong: 2,
        maxLat: 60,
        minLong: -10,
        minLat: 50,
    }
};

const Map = ({
    rappers,
    setVideo,
    video,
    isMobile,
    season,
    selectedIndex,
    setSelectedIndex
}) => {
    const [viewport, setViewport] = useState ({
        ...initialViewport,
    });
    const [points, setPoints] = useState([]);
    const [error, setError] = useState(false);
    const [showWarning, setShowWarning] = useState(true);
    const [disablePan, setDisablePan] = useState(false);
    const [rapperOfTheWeek, setRapperOfTheWeek] = useState(null);
    const [rapperOfTheWeekData, setRapperOfTheWeekData] = useState(null);
    const mapRef = useRef();
    const mapContainerRef = useRef();

    useEffect(() => {
        if (isMobile) {
            initialViewport.zoom = 4;
            initialViewport.minZoom = 4;
            mapStyle.height = '200px'
        } else {
            initialViewport.zoom = 5;
            initialViewport.minZoom = 5;
            mapStyle.height = '95vh';
            mapStyle.height = 'calc(var(--vh, 1vh) * 95)';
        }

        setViewport(initialViewport);
        setDisablePan(isMobile);
    }, [isMobile]);

    const openPopup = index => {
        setSelectedIndex(index);
        setShowWarning(false);
    };

    const closePopup = () => {
        setSelectedIndex(null);
        window.history.pushState(
            null,
            null,
            `${window.location.origin}${window.location.pathname}`
        );
    };

    const togglePan = () => {
        if (disablePan) mapContainerRef.current.scrollIntoView(true);
        mapStyle.height = disablePan ? 'calc(calc(var(--vh, 1vh) * 100) - 30px)' : '200px';
        setDisablePan(!disablePan);
    };

    useEffect(() => {
        if (video && points && points.length > 0) {
            openPopup(video.id);
        }
        // eslint-disable-next-line
    }, [video, points]);

    useEffect(() => {
        if (mapRef.current) {
            // remove tabindex to prevent multiple readings on screen readers
            mapRef.current._eventCanvasRef.current.tabIndex = -1
        }
    }, [mapRef]);

    const createRapperObjects = async () => {
        const promises = rappers && rappers.map(async rapper => {
            const {
                outcode,
                id,
                pick_of_the_week: pickOfTheWeek,
            } = rapper;

            if (!outcode || !id) {
                return false;
            }

            if (pickOfTheWeek) {
                setRapperOfTheWeek(rapper);
            }

            const res = await fetch(`https://api.postcodes.io/outcodes/${outcode}`);
            const data = await res.json();

            if (data.status === 404) {
                return false;
            }

            const { longitude, latitude } = data.result;
            // Randomising this somewhat so that if two pins have the exact same lat/long
            // they will still split apart
            const long = Number(longitude.toFixed(6)) + Number(Math.random().toFixed(2) * 0.0075);
            const lat  = Number(latitude.toFixed(6)) + Number(Math.random().toFixed(2) * 0.0075);

            if (pickOfTheWeek) {
                setRapperOfTheWeekData({
                    "type": "Feature",
                    "properties": {
                        "cluster": false,
                        "rapperId": id,
                        "category": "Rap"
                    },
                    "geometry": {
                        "type": "Point",
                        "coordinates": [
                            long,
                            lat,
                        ]
                    },
                    ...rapper,
                });
            }

            return {
                "type": "Feature",
                "properties": {
                    "cluster": false,
                    "rapperId": id,
                    "category": "Rap"
                },
                "geometry": {
                    "type": "Point",
                    "coordinates": [
                        long,
                        lat,
                    ]
                },
                ...rapper,
            }
        });

        if (promises) {
            return await Promise.all(promises);
        }
    };

    useEffect(() => {
        createRapperObjects()
            .then(data => {
                if (data) {
                    setPoints(data)
                }
            })
            .catch(err => console.error(err));

        // eslint-disable-next-line
    }, [rappers]);

    const boundsToConcat = mapRef.current
        ? mapRef.current
            .getMap()
            .getBounds()
            .toArray()
        : null;

    const bounds = [].concat.apply([], boundsToConcat);

    const { clusters, supercluster } = useSupercluster({
        points,
        bounds,
        zoom: viewport.zoom,
        options: {
            radius: 45,
            maxZoom: initialViewport.maxZoom,
            minZoom: initialViewport.minZoom,
        }
    });

    const markers = clusters.map(cluster => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const { cluster: isCluster, point_count: pointCount } = cluster.properties;
        const { id } = cluster;

        if (isCluster) {
            return (
                <CustomMarker
                    key={id}
                    index={id}
                    season={season}
                    latitude={latitude}
                    longitude={longitude}
                    pointCount={pointCount}
                    location={`Cluster of ${pointCount}`}
                    isCluster
                    onClick={() => {
                        const expansionZoom = Math.min(
                            supercluster.getClusterExpansionZoom(id),
                            20
                        );

                        setViewport({
                            ...viewport,
                            latitude,
                            longitude,
                            zoom: expansionZoom,
                            transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
                            transitionDuration: 'auto',
                        });
                    }}
                />
            )
        } else {
            const {
                id,
                name,
                location,
                is_contestant: isContestant,
                info,
                geometry: {
                    coordinates,
                },
                image_url: imageURL,
                pick_of_the_week: pickOfTheWeek,
            } = cluster;
            const [longitude, latitude] = coordinates;

            return (
                <CustomMarker
                    key={id}
                    index={id}
                    season={season}
                    id={id}
                    longitude={longitude}
                    latitude={latitude}
                    name={name}
                    location={location}
                    info={info}
                    onClick={openPopup}
                    setViewport={setViewport}
                    viewport={viewport}
                    isContestant={isContestant}
                    imageURL={imageURL}
                    pickOfTheWeek={pickOfTheWeek}
                />
            )
        }
    });

    return (
        <>
            {error
                ? <Error />
                : <section className={`Map ${disablePan ? 'disabled' : ''}`} ref={mapContainerRef}>
                    <h2 className="vh">The UK Rap Map</h2>
                    <div className={`Map__DisablePan ${disablePan ? '' : 'hidden'}`}>
                        <button onClick={togglePan}>Explore the Map</button>
                    </div>

                    <ReactMapGL
                        className="Map__Canvas"
                        mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_TOKEN}
                        mapStyle={season === 3
                            ? 'mapbox://styles/bbccreativerapgame/ckr27whgn8fia18qvwgr1d6sv'
                            : 'mapbox://styles/bbccreativerapgame/ckf6plioj1maq19oubg7rawnf'
                        }
                        onViewportChange={nextViewport => {
                            if (nextViewport.zoom > 14.75) {
                                return false;
                            } else if (nextViewport.zoom < initialViewport.minZoom) {
                                return false;
                            } else if (nextViewport.latitude > initialViewport.boundArea.maxLat) {
                                return false;
                            } else if (nextViewport.latitude < initialViewport.boundArea.minLat) {
                                return false;
                            } else if (nextViewport.longitude > initialViewport.boundArea.maxLong) {
                                return false;
                            } else if (nextViewport.longitude < initialViewport.boundArea.minLong) {
                                return false;
                            } else if (nextViewport.zoom < 7 && !selectedIndex && !showWarning) {
                                setViewport(nextViewport);
                                setShowWarning(true);
                            } else if (nextViewport.zoom >= 7 && showWarning) {
                                setViewport(nextViewport);
                                setShowWarning(false);
                            } else {
                                setViewport(nextViewport);
                            }
                        }}
                        {...viewport}
                        {...mapStyle}
                        dragPan={!disablePan}
                        ref={mapRef}
                        onLoad={() => {}} // Could have an overlay while loading...
                        onError={() => setError(true)}
                        touchZoom={true}
                    >
                        {markers}

                        {selectedIndex !== null
                        && points.filter(point => point.id === selectedIndex).length > 0
                            ? <CustomPopup
                                index={selectedIndex}
                                marker={points.filter(point => point.id === selectedIndex)}
                                closePopup={closePopup}
                                setVideo={setVideo}
                                season={season}
                                rapperOfTheWeek={rapperOfTheWeek}
                            />
                            : ''
                        }

                        <div className="Map__Controls">
                            <button
                                className={`Map__Controls-compress ${disablePan ? 'hidden' : ''}`}
                                onClick={togglePan}
                                title="Shrink Map window"
                                aria-label="Shrink Map window"
                            >
                                <FontAwesomeIcon icon={faCompress} />
                            </button>
                            <NavigationControl />
                            <button
                                className="Map__Controls-reset"
                                title="Reset all map zoom and positioning"
                                aria-label="Reset all map zoom and positioning"
                                onClick={() => {
                                    setViewport({ ...initialViewport });
                                    setShowWarning(true);
                                }}
                            >
                                <FontAwesomeIcon icon={faRedo} />
                            </button>
                        </div>

                        <p className={`Map__ContentWarning ${showWarning ? '' : 'hidden'}`}>
                            <span className="Map__ContentWarning-G">G</span>
                            Guidance warning: adult themes and language
                        </p>

                        {season === 3
                        && rapperOfTheWeek
                        && rapperOfTheWeekData
                            ? <RapperOfTheWeekButton
                                rapperOfTheWeek={rapperOfTheWeek}
                                rapperOfTheWeekData={rapperOfTheWeekData}
                                setSelectedIndex={setSelectedIndex}
                                setViewport={setViewport}
                                viewport={viewport}
                                FlyToInterpolator={FlyToInterpolator}
                                showWarning={showWarning}
                            />
                            : ''
                        }
                    </ReactMapGL>

                    <div className="Map__Border Map__Border-Top" style={{ backgroundImage: `url('${borderTopImage}')`}}> </div>
                    <div className="Map__Border Map__Border-Right" style={{ backgroundImage: `url('${borderRightImage}')`}}> </div>
                    <div className="Map__Border Map__Border-Bottom" style={{ backgroundImage: `url('${borderTopImage}')`}}> </div>
                    <div className="Map__Border Map__Border-Left" style={{ backgroundImage: `url('${borderRightImage}')`}}> </div>
                </section>
            }
        </>
    )
};

export default Map;
